import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import supabase from '@/lib/supabase';

interface PostRow {
  id: number;
  title: string;
  content: string;
  author: string;
  category: string;
  views: number;
  created_at: string;
  user_id: string | null;
  is_hidden: boolean;
}

export default function PostsSection() {
  const [posts, setPosts] = useState<PostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [commentCounts, setCommentCounts] = useState<Record<number, number>>({});

  const fetchPosts = async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (search.trim()) {
        const term = search.trim();
        query = query.or(`title.ilike.%${term}%,author.ilike.%${term}%`);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const fetchedPosts = (data || []) as PostRow[];
      setPosts(fetchedPosts);

      if (fetchedPosts.length > 0) {
        const postIds = fetchedPosts.map((p) => p.id);
        const { data: commentData } = await supabase
          .from('comments')
          .select('post_id')
          .in('post_id', postIds);

        const counts: Record<number, number> = {};
        (commentData || []).forEach((c: { post_id: number }) => {
          counts[c.post_id] = (counts[c.post_id] || 0) + 1;
        });
        setCommentCounts(counts);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setError(message || '게시물 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchPosts();
  };

  const handleToggleHide = async (post: PostRow) => {
    setActionLoading(post.id);
    try {
      const { error: updateError } = await supabase
        .from('posts')
        .update({ is_hidden: !post.is_hidden })
        .eq('id', post.id);

      if (updateError) throw updateError;

      setPosts((prev) =>
        prev.map((p) => (p.id === post.id ? { ...p, is_hidden: !p.is_hidden } : p))
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setError(message || '상태 변경 중 오류가 발생했습니다.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (post: PostRow) => {
    if (!confirm(`"${post.title}" 게시글을 정말 삭제하시겠습니까?`)) return;

    setActionLoading(post.id);
    try {
      const { error: deleteError } = await supabase
        .from('posts')
        .delete()
        .eq('id', post.id);

      if (deleteError) throw deleteError;

      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setError(message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr: string): string => {
    try {
      const d = new Date(dateStr);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}.${m}.${day}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">게시물 관리</h2>
          <p className="text-sm text-foreground-500">전체 게시물을 검토하고 관리합니다</p>
        </div>
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="제목 또는 작성자 검색"
              className="px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500 w-56"
            />
            <button
              type="submit"
              className="px-3 py-2 text-sm font-medium bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-colors whitespace-nowrap"
            >
              검색
            </button>
          </form>
          <button
            onClick={fetchPosts}
            className="px-3 py-2 text-sm font-medium bg-white border border-foreground-200 hover:bg-background-50 text-foreground-700 rounded-md transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-20 bg-white rounded-lg border border-foreground-200">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-foreground-500">게시물 목록을 불러오는 중...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-lg border border-foreground-200">
          <div className="w-14 h-14 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-article-line text-2xl text-foreground-400" />
          </div>
          <h3 className="text-base font-bold text-foreground-950 mb-1">
            {search.trim() ? '검색 결과가 없습니다' : '등록된 게시물이 없습니다'}
          </h3>
          <p className="text-sm text-foreground-500">
            {search.trim() ? '다른 검색어로 다시 시도해보세요.' : '첫 번째 게시글을 작성해보세요.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-foreground-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-foreground-200 bg-background-100">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">제목</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">카테고리</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">작성자</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">조회</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">댓글</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">작성일</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">상태</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">관리</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => (
                  <tr key={p.id} className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 min-w-0">
                        {p.is_hidden && (
                          <i className="ri-eye-off-line text-xs text-foreground-400 shrink-0" />
                        )}
                        <Link
                          to={`/board/${p.id}`}
                          className={`text-sm font-medium hover:text-primary-500 transition-colors truncate block max-w-[300px] ${
                            p.is_hidden ? 'text-foreground-400' : 'text-foreground-950'
                          }`}
                        >
                          {p.title}
                        </Link>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-foreground-600">{p.category}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-foreground-600">{p.author}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-500 text-center">
                      {p.views.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-500 text-center">
                      {(commentCounts[p.id] || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">
                      {formatDate(p.created_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                          p.is_hidden
                            ? 'bg-foreground-200 text-foreground-500'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {p.is_hidden ? '숨김' : '공개'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <Link
                          to={`/board/${p.id}`}
                          className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
                        >
                          보기
                        </Link>
                        <button
                          onClick={() => handleToggleHide(p)}
                          disabled={actionLoading === p.id}
                          className="text-xs text-foreground-500 hover:text-foreground-700 font-medium whitespace-nowrap disabled:opacity-50"
                        >
                          {p.is_hidden ? '공개' : '숨김'}
                        </button>
                        <button
                          onClick={() => handleDelete(p)}
                          disabled={actionLoading === p.id}
                          className="text-xs text-red-500 hover:text-red-600 font-medium whitespace-nowrap disabled:opacity-50"
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}